/*
 * File: AskQuestionModal.jsx
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: September 26, 2024 at 9:33 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useState, useRef, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import validator from 'validator';
import { useSelector } from 'react-redux';
import { Howl } from 'howler';

// Utilities
import { toastError } from '../../utilities/toaster';

// Services
import { submitQuestion } from '../../services/content';

// Components
import { Button } from '../Button';
import { Modal } from '../Modal';
import { Typography } from '../Typography';
import { TextArea } from '../TextArea';
import { ConfettiBurst } from '../ConfettiBurst';

// Styles
import * as S from './AskQuestionModal.styles';


/**
 * Component
 */

export const AskQuestionModal = ({
	className, isOpen, handleClose
}) => {

	// Create state handlers
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fireConfetti, setFireConfetti] = useState(false);
	const [questionError, setQuestionError] = useState(null);
	const [inputValues, setInputValues] = useState({});
	const [showConfirmation, setShowConfirmation] = useState(false);

	// Create refs
	const confettiTargetRef = useRef();

	// Create sound hooks
	const fanfareSound = useMemo(() => new Howl({
		src: [`${process.env.CDN_URL}/public/assets/audio/fanfare.mp3`],
		html5: true,
		volume: 0.1
	}), []);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Handle submit action
	const handleSubmit = async () => {

		// Get parameters
		const { question } = inputValues;

		// Validate parameters
		if (!question || validator.isEmpty(question, { ignore_whitespace: true })) {
			setQuestionError({ message: 'Please enter a question for our team' });
			return;
		}

		// Update submission state
		setIsSubmitting(true);

		// Submit question
		try {

			// Submit question
			await submitQuestion({ question });

			// Update submission state
			setIsSubmitting(false);

			// Show confirmation
			setShowConfirmation(true);

			// Fire confetti
			setFireConfetti(true);

			// Reset confetti
			setTimeout(() => {
				setFireConfetti(false);
			}, 4000);

		} catch (e) {

			// Update submission state
			setIsSubmitting(false);

			// Show error message
			toastError(uiMode, 'Whoops. We\'re having trouble submitting your question. Give it another go.');
		}
	};

	// Handle on input change action
	const handleOnChange = (event) => {
		const {
			name, value
		} = event.target;
		setInputValues({ ...inputValues, [name]: value });
	};

	// Handle actions on component load
	useEffect(() => {
		if (isOpen) {

			// Reset state
			setShowConfirmation(false);
			setInputValues({});
		}
	}, [isOpen]);

	// Render component
	return (
		<Modal className={className} isOpen={isOpen} handleClose={handleClose} showClose variant="large" clickOutsideClose useWrapper>
			<S.ModalInner>

				{/* Submission Panel */}
				{!showConfirmation && (
					<S.SubmissionPanel>

						{/* Icon */}
						<S.IconContainer><FontAwesomeIcon icon={['fas', 'question']} /></S.IconContainer>

						{/* Content */}
						<Typography tag="h3" weight="bold">Ask a Question</Typography>
						<Typography tag="p" variation="1" weight="light" className="subtitle">Submit a question for our team of experts to answer.</Typography>

						{/* Text Area */}
						<TextArea
							placeholder="What's on your mind?"
							name="question"
							type="text"
							rows={7}
							error={questionError}
							value={inputValues.question || ''}
							containerClassName="formInput"
							onFocus={() => { setQuestionError(null); }}
							onKeyUp={() => { setQuestionError(null); }}
							onBlur={() => { setQuestionError(null); }}
							onChange={handleOnChange}
						/>

						{/* Actions */}
						<S.ActionContainer>
							<Button disabled={isSubmitting} isLoading={isSubmitting} variant="solid" size="medium" onClick={handleSubmit}>
								<Typography tag="p" variant="2" weight="medium">Submit</Typography>
							</Button>
							<S.TextAreaPadding />
						</S.ActionContainer>

					</S.SubmissionPanel>
				)}

				{/* Confirmation Panel */}
				{showConfirmation && (
					<S.ConfirmationPanel>

						{/* Icon */}
						<S.ConfirmationIconContainer>
							<FontAwesomeIcon icon={['fal', 'check-circle']} />
							<S.ConfettiTarget ref={confettiTargetRef} />
						</S.ConfirmationIconContainer>

						{/* Title */}
						<Typography tag="h3" weight="bold">Thanks for your question!</Typography>

						{/* Instructions */}
						<Typography tag="p" variation="1" weight="medium">
							Your question has been submitted. Questions like yours will help us create a better app experience.
						</Typography>

						{/* Next Button */}
						<Button onClick={handleClose} size="large">Complete</Button>

					</S.ConfirmationPanel>
				)}

				{/* Confetti */}
				{fireConfetti && (
					<ConfettiBurst
						fireAway={fireConfetti}
						targetRef={confettiTargetRef}
						force={0.4}
						duration={3000}
						particleCount={80}
						audioHook={fanfareSound}
					/>
				)}

			</S.ModalInner>
			{showConfirmation && <S.ConfirmationBackground />}
		</Modal>
	);
};


/**
 * Configuration
 */

AskQuestionModal.displayName = 'AskQuestionModal';
AskQuestionModal.propTypes = {
	className: PropTypes.string,
	isOpen: PropTypes.bool,
	handleClose: PropTypes.func
};
AskQuestionModal.defaultProps = {
	className: null,
	isOpen: false,
	handleClose: null
};
